import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { BarLoader, Button, BUTTON_THEME, IconButton, Input } from '@st-operations/uikit/src/v2'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1'
import { BUTTON_COLOR } from '@st-operations/uikit/src/v2'

import {
  getOrgPendingInvitations,
  inviteUser,
  deleteOrgPendingInvitation,
} from '_services/predactiv-admin'
import Select from '_components/select'

import Table from '../table'

const DeleteInvitationModal = ({ invitation, onClose, onConfirm }) => {
  const handleDelete = () => {
    onConfirm(invitation)
    setTimeout(onClose, 200)
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <h2 className="text-center text-lg font-bold mb-4">Are you sure?</h2>
      <div className="mb-4">
        You are deleting the invitation for <b>{invitation.invitedUser}</b>.
      </div>
      <div className="flex justify-end gap-2">
        <Button onClick={onClose} theme={BUTTON_THEME.GHOST}>
          Cancel
        </Button>
        <Button onClick={handleDelete} color={BUTTON_COLOR.RED}>
          Continue
        </Button>
      </div>
    </Dialog>
  )
}

const ROLE_OPTIONS = [
  { label: 'Admin', value: 'ADMIN' },
  { label: 'Member', value: 'MEMBER' },
]

const InvitationsManager = ({ organizationId, env }) => {
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [newInvite, setNewInvite] = useState({ email: '', role: ROLE_OPTIONS[1] })
  const [emailError, setEmailError] = useState('')
  const [invitationToDelete, setInvitationToDelete] = useState(null)

  const fetchPendingInvitations = async () => {
    setIsLoading(true)
    try {
      const response = await getOrgPendingInvitations({ env, organizationId })
      setPendingInvitations(response)
    } catch (error) {
      console.error('Error fetching pending invitations:', error)
      toast.error('Failed to fetch pending invitations')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPendingInvitations()
  }, [organizationId, env])

  const handleDeleteInvitation = async (invitation) => {
    try {
      await deleteOrgPendingInvitation({
        env,
        id: invitation._id,
        organizationId,
        invitedUser: invitation.invitedUser,
      })
      toast.success('Invitation deleted successfully')
      fetchPendingInvitations()
    } catch (error) {
      console.error('Error deleting invitation:', error)
      toast.error('Failed to delete invitation')
    }
  }

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return re.test(String(email).toLowerCase())
  }

  const handleInviteUser = async () => {
    const trimmedEmail = newInvite.email.trim()
    if (!validateEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address')
      return
    }
    setEmailError('')

    try {
      await inviteUser({
        env,
        invitedUser: trimmedEmail,
        organizationId,
        role: newInvite.role.value,
      })
      toast.success('User invited successfully')
      setNewInvite({ email: '', role: ROLE_OPTIONS[1] })
      fetchPendingInvitations()
    } catch (error) {
      console.error('Error inviting user:', error)
      toast.error(error.message || 'Failed to invite user')
    }
  }

  const renderAction = (data) => (
    <IconButton
      onClick={() => setInvitationToDelete(data)}
      aria-label="Delete Invitation"
      iconProps={{
        icon: 'trash',
        type: 'outline',
        width: 16,
        height: 16,
        color: '#3D3C68',
      }}
    />
  )

  if (isLoading) return <BarLoader />

  return (
    <div className="apa-org__invitations">
      <h3>Pending Invitations</h3>
      <div className="apa-org__invite-form">
        <Input
          label="Email"
          value={newInvite.email}
          onChange={(value) => {
            setNewInvite({ ...newInvite, email: value })
            setEmailError('')
          }}
          error={emailError}
        />
        <Select
          label="Role"
          value={newInvite.role}
          options={ROLE_OPTIONS}
          onChange={(value) => setNewInvite({ ...newInvite, role: value })}
        />
        <Button onClick={handleInviteUser} theme={BUTTON_THEME.PRIMARY}>
          Invite User
        </Button>
      </div>
      <Table
        items={pendingInvitations.map((item) => ({
          data: item,
          values: [
            item.invitedUser,
            item.invitingUser,
            dayjs(item.createdAt).format('MMM D, YYYY HH:mm'),
            item.role,
          ],
        }))}
        header={['Invited User', 'Inviting User', 'Created At', 'Role']}
        renderAction={renderAction}
        gridTemplateColumns="1fr 1fr 1fr 1fr auto"
      />
      <If condition={invitationToDelete}>
        <DeleteInvitationModal
          invitation={invitationToDelete}
          onClose={() => setInvitationToDelete(null)}
          onConfirm={handleDeleteInvitation}
        />
      </If>
    </div>
  )
}

InvitationsManager.propTypes = {
  organizationId: PropTypes.string.isRequired,
  env: PropTypes.string.isRequired,
}

export default InvitationsManager
